import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Api from "services/api";
import { updateAdmin, adminSelector, setError } from "slices/adminSlice";
import ErrorMessage from "components/common/ErrorMessage";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import FullPageLoader from "components/common/FullPageLoader";

const EditAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error, loader } = useSelector(adminSelector);
  const [loading, setLoading] = React.useState(true);
  const { handleSubmit, control, watch, reset, formState: { errors } } = useForm({
    defaultValues: { name: "", email: "", password: "", password_confirmation: "" },
  });

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    Api.fetchAdmin(id)
      .then((res) => {
        const data = res?.data?.data;
        if (!active) return;
        if (res?.status >= 200 && res?.status < 300 && data) {
          if (data.is_protected) {
            toast.error("This admin cannot be edited.", { className: "errorToast" });
            navigate("/admins");
            return;
          }
          reset({ name: data.name || "", email: data.email || "", password: "", password_confirmation: "" });
        } else {
          toast.error(res?.data?.message || "Admin not found.", { className: "errorToast" });
          navigate("/admins");
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id]);

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message || "Admin updated successfully.", { className: "successToast" });
      dispatch(setError({}));
      navigate("/admins");
    } else {
      toast.error(message || "Could not update admin.", { className: "errorToast" });
    }
  };

  const onSubmit = (data) => {
    dispatch(setError({}));
    const fd = new FormData();
    fd.append("name", data.name);
    fd.append("email", data.email);
    // Only send password when the user wants to change it
    if (data.password) {
      fd.append("password", data.password);
      fd.append("password_confirmation", data.password_confirmation);
    }
    dispatch(updateAdmin(fd, id, submitCallback));
  };

  return (
    <div className="content">
      {(loading || loader) && <FullPageLoader />}
      <div className="row_main_head">
        <button className="main_heading back_button" onClick={() => navigate(-1)}>Back</button>
        <h2 className="main_heading">Edit Admin</h2>
      </div>

      <div className="card_table p-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row-primary">
            <h2>Name *</h2>
            <div className="w-100">
              <Controller
                name="name"
                control={control}
                rules={{ required: "Please enter a name" }}
                render={({ field }) => (
                  <input className="input-in type_inp" type="text" placeholder="Name" {...field} />
                )}
              />
              {errors?.name?.message && <ErrorMessage text={errors.name.message} />}
              {error?.errors?.name?.map((e, i) => <ErrorMessage key={i} text={e} />)}
            </div>
          </div>

          <div className="row-primary">
            <h2>Email *</h2>
            <div className="w-100">
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Please enter an email",
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
                }}
                render={({ field }) => (
                  <input className="input-in type_inp" type="email" placeholder="Email" {...field} />
                )}
              />
              {errors?.email?.message && <ErrorMessage text={errors.email.message} />}
              {error?.errors?.email?.map((e, i) => <ErrorMessage key={i} text={e} />)}
            </div>
          </div>

          <div className="row-primary">
            <h2>Password</h2>
            <div className="w-100">
              <Controller
                name="password"
                control={control}
                rules={{ minLength: { value: 8, message: "Minimum 8 characters" } }}
                render={({ field }) => (
                  <input className="input-in type_inp" type="password" placeholder="Leave blank to keep current password" {...field} />
                )}
              />
              {errors?.password?.message && <ErrorMessage text={errors.password.message} />}
              {error?.errors?.password?.map((e, i) => <ErrorMessage key={i} text={e} />)}
            </div>
          </div>

          <div className="row-primary">
            <h2>Confirm Password</h2>
            <div className="w-100">
              <Controller
                name="password_confirmation"
                control={control}
                rules={{ validate: (val) => !watch("password") || val === watch("password") || "Passwords do not match" }}
                render={({ field }) => (
                  <input className="input-in type_inp" type="password" placeholder="Confirm Password" {...field} />
                )}
              />
              {errors?.password_confirmation?.message && <ErrorMessage text={errors.password_confirmation.message} />}
            </div>
          </div>

          <div className="Form-footer">
            <button type="button" className="Cancle_button" onClick={() => navigate("/admins")}>Cancel</button>
            <SubmitButton loader={loader} btnText="Update" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdmin;
